import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import Overview from "../../../components/ProjectDetails/Overview";
import Member from "../../../components/ProjectDetails/Member";
import MemberAvailability from "../../../components/ProjectDetails/MemberAvailability";
import Requirement from "../../../components/ProjectDetails/Requirement";
import Technologies from "../../../components/ProjectDetails/Technologies";
import Select from "react-select";
import { toast } from "react-toastify";
import ModalLayout from "../../../components/Common/ModalLayout";
import ProjectCreate from "./ProjectCreate";
import ProjectFiles from "../../../components/ProjectDetails/ProjectDocuments";
import { useSelector, useDispatch } from "react-redux";
import Tasks from "../../../components/ProjectDetails/Task/Tasks";
import "../../../css/styles.css";
import { MobileHeader } from "../../../components/ProjectDetails/Layout/MobileHeader";
import { canViewModule, getModulePermissions } from "../../../utils/moduleAccess";

const ProjectDetails = ({ isDeveloper }) => {
  const location = useLocation();
  const [projectStatus, setProjectStatus] = useState("");
  const [status, setStatus] = useState(location.pathname.split("/")[3]);
  const [isLoading, setLoading] = useState(false);
  const [open, SetOpenModal] = useState(false);
  const [computedStatusCounts, setComputedStatusCounts] = useState({});
  const [toggle, setToggle] = useState("task");
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [milestoneStatusCount, setMilestoneStatusCount] = useState({});

  const DetailedProjectData = useSelector(
    (state) => state.projectDetails.getDetailedProjectData
  );

  const [taskList, setTaskList] = useState([]);

  const usersData = useSelector((state) => state.auth.userInfoData);
  const authState = useSelector((state) => state.auth);
  const modulePermissions = useMemo(
    () => getModulePermissions(authState),
    [authState]
  );
  const canViewProject = canViewModule(modulePermissions, "project");

  const dispatch = useDispatch();
  const [tabStatus, setTabStatus] = useState("");
  const [openMenu, setOpenMenu] = useState(false);
  const [permissionData, setPermissionData] = useState({});
  const [tabTitle, setTabTitle] = useState([
    {
      id: "1",
      label: "Overview",
      status: "overview",
    },
    {
      id: "2",
      label: "Requirement",
      status: "requirement",
    },
    {
      id: "3",
      label: "Technologies",
      status: "technologies",
    },
    {
      id: "6",
      label: "Files",
      status: "files",
    },
    {
      id: "7",
      label: "Member",
      status: "member",
    },
    {
      id: "8",
      label: "Tasks",
      status: "tasks",
    },
  ]);

  useEffect(() => {
    const fetchData = () => {
      if (canViewProject) {
        setLoading(true);
        if (permissionData && Object.keys(permissionData).length > 0) {
          const isViewMilestoneAllowed = permissionData?.permissions?.find(
            (p) => p.action === "View-Milestone-And-Tasks"
          )?.allowed;
          const updatedTabTitle = isViewMilestoneAllowed
            ? tabTitle
            : tabTitle.filter((item) => item.label !== "Tasks");

          setTabTitle(updatedTabTitle);
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [permissionData]);

  useEffect(() => {
    setTabStatus(location.pathname.split("/")[3]);
  }, [location]);

  const params = useParams();
  const projectname =
    DetailedProjectData &&
    DetailedProjectData.project &&
    DetailedProjectData.project.name;

  const handleModal = () => {
    SetOpenModal(!open);
  };

  useEffect(() => {
    const loadProjectData = async () => {
      if (!params?.name) return;
      try {
        setLoading(true);
        let projectObj = DetailedProjectData?.project;

        if (
          !projectObj ||
          (projectObj.name !== params.name && projectObj._id !== params.name)
        ) {
          const detailRes = await dispatch.projectDetails.getProjectDetailes({
            projectName: params.name,
          });
          projectObj =
            detailRes?.project || detailRes?.data?.project || detailRes;
        }

        const realProjectId = projectObj?._id || projectObj?.id;

        if (realProjectId) {
          const milestoneResponse =
            await dispatch.newProjectDetails.getGetFirstMilestone({
              project_id: realProjectId,
            });
          const milestoneData = milestoneResponse?.data;

          const groupTasks = milestoneData?.groups
            ? milestoneData.groups.flatMap((group) => group.tasks || [])
            : [];
          const allTasks = [...(milestoneData?.tasks || []), ...groupTasks];

          const statusCount = allTasks.reduce((acc, task) => {
            const status = task?.task_status?.task_status || "Unknown";
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});
          setComputedStatusCounts(statusCount);

          if (canViewProject && usersData?._id) {
            const getPermissions =
              await dispatch.permissions.getUserPermissions({
                user_id: usersData._id,
                project_id: realProjectId,
              });
            setPermissionData(getPermissions?.data);
          }

          const getTasks = await dispatch.projectDetails.getTasksList({
            project_id: realProjectId,
          });
          setTaskList(getTasks);
        }
      } catch (err) {
        console.log("Error loading project details:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [params?.name, canViewProject, usersData?._id]);

  useEffect(() => {
    const fetchMilestoneTasks = async () => {
      if (selectedOption && DetailedProjectData?.project?._id) {
        try {
          const res = await dispatch.projectDetails.getTasksByMilestone({
            project_id: DetailedProjectData?.project?._id,
            milestone_id: selectedOption,
          });
          const milestoneTasks = res?.data || [];
          const statusCount = milestoneTasks.reduce((acc, task) => {
            const st = task?.task_status?.task_status || "Unknown";
            acc[st] = (acc[st] || 0) + 1;
            return acc;
          }, {});
          setMilestoneStatusCount(statusCount);
        } catch (err) {
          console.log("Error fetching milestone tasks:", err);
        }
      }
    };
    fetchMilestoneTasks();
  }, [selectedOption, DetailedProjectData?.project?._id]);



  const tabDetail = (DetailedProjectData, isDeveloper) => {
    switch (tabStatus) {
      case "overview":
        return (
          <Overview
            DetailedProjectData={DetailedProjectData}
            isDeveloper={isDeveloper}
          />
        );
      case "requirement":
        return <Requirement DetailedProjectData={DetailedProjectData} />;
      case "technologies":
        return <Technologies DetailedProjectData={DetailedProjectData} />;
      case "files":
        return <ProjectFiles DetailedProjectData={DetailedProjectData} />;
      case "member":
        return (
          <Member
            isDeveloper={isDeveloper}
            data={DetailedProjectData}
            usersData={usersData}
          />
        );
      case "memberavailability":
        return <MemberAvailability data={DetailedProjectData} />;
      case "tasks":
        return <Tasks data={DetailedProjectData} />;
      default:
        return (
          <Overview
            DetailedProjectData={DetailedProjectData}
            isDeveloper={isDeveloper}
          />
        );
    }
  };

  const updateProjectStatus = async () => {
    try {
      setLoading(true);
      const responseData = await dispatch.projectDetails.projectStatusChange({
        project_id: DetailedProjectData.project._id,
        status: projectStatus,
      });
      if (responseData && responseData.success) {
        toast.success("Project status updated");
      } else {
        toast.error(responseData.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.error);
    }
  };

  return (
    <div className="pd-theme min-h-screen bg-theme-cream">
      {/* Sub Header Navigation Tabs (Left Aligned like KPI) */}
      <div className="bg-theme-paper border-b border-theme-cream-3 mb-4">
        <div className="container mx-auto">
          <div className="flex items-center gap-5 sm:gap-8 h-10 sm:h-12 overflow-x-auto max-w-full scrollbar-none whitespace-nowrap">
            {tabTitle &&
              tabTitle.map((item) => {
                const isTabActive = item.status === status;
                return (
                  <Link
                    key={item.id}
                    to={`/projects/${params?.name}/${item.status}`}
                    className="h-full flex items-center !no-underline"
                    onClick={() => setStatus(item.status)}
                  >
                    <div
                      className={`flex items-center h-full gap-1.5 text-xs sm:text-sm border-b-2 transition-all shrink-0 cursor-pointer ${
                        isTabActive
                          ? "border-theme-ink !text-theme-fg-1 font-semibold"
                          : "border-transparent text-theme-fg-3 hover:text-theme-fg-1 font-medium"
                      }`}
                    >
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>

      <div className="container mx-auto">
        {/* Breadcrumb below sub-header */}
        {/* <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 mb-10 pt-1">
          <NavLink
            to="/projects"
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 font-medium !no-underline transition-colors"
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 0L7.41 1.41L2.83 6L7.41 10.59L6 12L2.86102e-06 6L6 0Z"
                fill="currentColor"
              />
            </svg>
            <span>Projects</span>
          </NavLink>
          <span className="text-slate-300 font-medium">/</span>
          <span className="text-slate-900 font-bold truncate">
            {projectname || params?.name}
          </span>
        </div> */}
          <div className={`flex flex-col lg:flex-row lg:mt-10`}>
            <div
              className={`mb-5 lg:mb-0  ${
                !["files", "requirement", "technologies"].includes(status)
                  ? "md:w-full"
                  : "md:w-full  w-full"
              } `}
            >
              <div
                className={` ${status === "requirement" ? "py-0 " : "py-7"}`}
              >
                {DetailedProjectData
                  ? tabDetail(DetailedProjectData, isDeveloper)
                  : null}
              </div>
            </div>

            <div className="hidden md:block">
              {!["files", "requirement", "technologies"].includes(status) ? (
                <div className=" mt-10 pl-4 ">
                  <div className="border border-theme-cream-3 rounded-lg h-auto px-2 py-4 bg-theme-cream-2">
                    <div className="flex items-center justify-between pb-4 space-x-2 px-3 cursor-pointer gap-10">
                      <p className="text-2xl !font-light text-nowrap text-theme-fg-1">
                        Tasks Status
                      </p>
                      <div className="bg-theme-cream-3 flex items-center justify-center gap-1 rounded-full p-1">
                        <p
                          className={`rounded-full px-3 py-2 text-xs cursor-pointer ${
                            toggle === "task"
                              ? "bg-theme-paper text-theme-fg-1"
                              : "bg-theme-cream-3 text-theme-fg-3"
                          }`}
                          onClick={() => {
                            setToggle("task");
                            setSelectedOption("");
                          }}
                        >
                          Tasks
                        </p>
                        <p
                          className={`rounded-full text-xs py-2 px-3 m-0 cursor-pointer ${
                            toggle === "milestone"
                              ? "bg-theme-paper text-theme-fg-1"
                              : "bg-theme-cream-3 text-theme-fg-3"
                          }`}
                          onClick={async () => {
                            setToggle("milestone");
                            if (DetailedProjectData?.project?._id) {
                              const getMileStone =
                                await dispatch.projectDetails.getMileStoneList({
                                  project_id: DetailedProjectData?.project?._id,
                                });
                              setMilestoneOptions(getMileStone || []);
                            }
                          }}
                        >
                          Milestones
                        </p>
                      </div>
                    </div>
                    {toggle === "milestone" && (
                      <div className="w-full px-4 pb-4">
                        <Select
                          placeholder="Select Milestone"
                          className="text-xs"
                          options={milestoneOptions?.map((item) => ({
                            value: item._id,
                            label: item.title,
                          }))}
                          onChange={(e) => setSelectedOption(e?.value || "")}
                        />
                      </div>
                    )}

                    <div className="pt-4 pl-5 self-start">
                      <div className="flex justify-left gap-2 mb-3 flex-col space-y-5">
                        <div className="flex gap-2 items-center">
                          <p className="text-sm !font-thin">
                            Todo(
                            {selectedOption !== ""
                              ? milestoneStatusCount?.ToDo ?? milestoneStatusCount?.Todo ?? 0
                              : computedStatusCounts?.ToDo ?? computedStatusCounts?.Todo ?? 0}
                            )
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-2 pl-5 self-start">
                      <div className="flex items-center justify-left gap-2 mb-3">
                        <p className="text-sm !font-thin ">
                          In Progress(
                          {selectedOption !== ""
                            ? milestoneStatusCount?.InProgress ?? 0
                            : computedStatusCounts?.InProgress ?? 0}
                          )
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 pl-5 self-start">
                      <div className="flex items-center justify-left gap-2 mb-3">
                        <p className="text-sm !font-thin ">
                          QA(
                          {selectedOption !== ""
                            ? milestoneStatusCount?.QA ?? 0
                            : computedStatusCounts?.QA ?? 0}
                          )
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 pl-5 self-start">
                      <div className="flex items-center justify-left gap-2 mb-3">
                        <p className="text-sm !font-thin ">
                          Production(
                          {selectedOption !== ""
                            ? milestoneStatusCount?.Production ?? 0
                            : computedStatusCounts?.Production ?? 0}
                          )
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <MobileHeader
          tabTitle={tabTitle}
          setOpenMenu={setOpenMenu}
          openMenu={openMenu}
          // projectName
        />

        <ModalLayout closeModal={handleModal} open={open}>
          <ProjectCreate
            closeModal={handleModal}
            projectInfoEdit={DetailedProjectData && DetailedProjectData.project}
          />
        </ModalLayout>
      </div>
  );
};

export default ProjectDetails;

